/**
 * Display-side parsing of the `@`-mention preamble that
 * use-companion-conversation's `withMentionPreamble` prepends to a sent
 * message. The model needs the full panel snapshot; the user only needs the
 * panel names (Chris, 2026-09-30), so the bubble shows chips + their words.
 */
const PREAMBLE_PREFIX = '[The user is referring to these panel(s)'
/** `"Title" (type: chart, id: abc)` — one per mentioned panel. */
const HEADER_RE = /^"(.*)" \(type: [^,]*, id: [^)]*\)\]?$/

export interface SplitMentionText {
  /** Panel titles named in the preamble, in order. */
  panels: string[]
  /** What the user actually typed. */
  body: string
  /** The preamble itself (panel snapshots for the model), or null. */
  context: string | null
}

export function splitMentionPreamble(text: string): SplitMentionText {
  if (!text.startsWith(PREAMBLE_PREFIX)) return { panels: [], body: text, context: null }
  const lines = text.split('\n')
  const panels: string[] = []
  // The preamble closes with `]` on its last line, then one blank line. A
  // snapshot line can also end in `]` before a blank separator, so the close
  // is the first such line NOT followed by another panel header.
  for (let i = 1; i < lines.length; i++) {
    const header = HEADER_RE.exec(lines[i])
    if (header) panels.push(header[1])
    if (lines[i].endsWith(']') && lines[i + 1] === '' && !HEADER_RE.test(lines[i + 2] ?? '')) {
      return {
        panels,
        body: lines.slice(i + 2).join('\n'),
        context: lines.slice(0, i + 1).join('\n'),
      }
    }
  }
  return { panels: [], body: text, context: null }
}
